/* eslint-disable react-refresh/only-export-components --
 * このファイルは「ルータ定義」であってコンポーネントモジュールではない。
 * react-refresh/only-export-components は「コンポーネントを宣言しているのに
 * コンポーネント以外(= router)も export している」ファイルを検出するルールだが、
 * createBrowserRouter に渡す lazy ページ群と RootLayout はここに置くほかなく、
 * 別ファイルへ移しても router 自体の export は残るため警告は消えない(= 移す意味がない)。
 * Fast Refresh はルート定義の編集時にどのみちフルリロードが要るので実害もない。
 * eslint-plugin-react-refresh 0.5 で localComponents 検出が入り 47 件出るように
 * なったため、行ごとの disable をやめてファイル単位で明示する (PRODUCT_DEV-629)。
 */
/**
 * React Router ルーティング設定
 *
 * createBrowserRouter でルート定義、`route.lazy` でコード分割を実装
 * （PRODUCT_DEV-703 で `React.lazy` + `Suspense` から移行。理由は lazyWithReload の説明を参照）
 */

import React, { Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useParams,
} from "react-router";
import { setRouterStateReader } from "@/lib/navDiagnostics";
import { lazyWithReload } from "@/lib/routeLazy";
import { installBackTracker, prepareHistory } from "@/lib/nav/backTracker";
import { readScreenHandle, screen } from "@/lib/nav/screens";
import { rootLoader } from "@/loaders/root-loader";
import { shouldRevalidateRoot } from "@/loaders/shouldRevalidateRoot";
import { MasterDataProvider } from "@/contexts/MasterDataContext";
import { PreferencesProvider } from "@/contexts/PreferencesContext";
import { ReportDeptLinkSync } from "@/hooks/useReportDept";
import { ChromeProvider } from "./components/shared/chrome/ChromeContext";
import { unizoChrome } from "./components/shared/chrome/unizoChrome";
import { useHome } from "@/hooks/useHome";
import { Splash } from "./components/shared/Splash";
import { ScrollToTop } from "./components/shared/ScrollToTop";
import NavOwnerSync from "./components/shared/chrome/NavOwnerSync";
import { EnvironmentBanner } from "./components/shared/EnvironmentBanner";
import { NavigationWatchdog } from "./components/shared/NavigationWatchdog";
import { NavigationProgress } from "./components/shared/NavigationProgress";
import { SavedMeetingToast } from "./components/shared/SavedMeetingToast";
import { SentCelebration } from "./components/shared/SentCelebration";
import { SysadminGuard } from "./components/shared/SysadminGuard";
import { RoleSplitDescendedGuard } from "./components/shared/RoleSplitDescendedGuard";
import {
  ROUTE_COMPANY_REPORT,
  ROUTE_MY_CUSTOMERS,
  ROUTE_MY_CUSTOMERS_DETAIL,
  ROUTE_GUEST_SCHEDULING,
  ROUTE_INQUIRIES_NEW,
} from "@/utils/const";

// ─────────────────────────────────────────────────────────────
// Lazy-loaded Pages
// ─────────────────────────────────────────────────────────────

const Recording = lazyWithReload(() => import("./pages/Recording/index"));
const SpeedRecording = lazyWithReload(() => import("./pages/SpeedRecording/index"));
const CopyRecordSetup = lazyWithReload(() => import("./pages/CopyRecordSetup/index"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard/index"));
const MeetingDetail = lazyWithReload(() => import("./pages/MeetingDetail/index"));
const MeetingGroupCreate = lazyWithReload(
  () => import("./pages/MeetingGroupCreate"),
);
const MeetingGroupDetail = lazyWithReload(
  () => import("./pages/MeetingGroupDetail"),
);
const Guide = lazyWithReload(() => import("./pages/Guide/index"));
const GuideArticleDetail = lazyWithReload(
  () => import("./pages/Guide/ArticleDetail"),
);
// PRODUCT_DEV-940: 場面から探すガイド（新版 guideRenewal だけ。Minizo には無い）
const GuideSceneDetail = lazyWithReload(
  () => import("./pages/Guide/SceneDetail"),
);
// PRODUCT_DEV-783: 初回オンボーディング（はじめての方へ）
const Onboarding = lazyWithReload(() => import("./pages/Onboarding/index"));
const Announcements = lazyWithReload(
  () => import("./pages/Announcements/index"),
);
const Trash = lazyWithReload(() => import("./pages/Trash/index"));
const Notifications = lazyWithReload(() => import("./pages/Notifications/index"));
// PRODUCT_DEV-635: 問い合わせ
const Inquiries = lazyWithReload(() => import("./pages/Inquiries/index"));
// PRODUCT_DEV-673: マイページ（導線なし・URL 直打ちで到達）
const MyPage = lazyWithReload(() => import("./pages/MyPage/index"));
const ActivitySummaryScreen = lazyWithReload(
  () => import("./pages/MyPage/Summary/index"),
);
const ArchiveScreen = lazyWithReload(() => import("./pages/MyPage/Archive/index"));
const SchedulingListScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/index"),
);
const SchedulingDetailScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/Detail"),
);
const SchedulingRescheduleScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/Reschedule"),
);
const SchedulingEditScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/Edit"),
);
const SchedulingNewScreen = lazyWithReload(
  () => import("./pages/MyPage/Scheduling/New"),
);
const GuestSchedulingScreen = lazyWithReload(
  () => import("./pages/GuestScheduling/index"),
);
const ReportScreen = lazyWithReload(() => import("./pages/MyPage/Report/index"));
const AdminSummaryScreen = lazyWithReload(
  () => import("./pages/MyPage/Admin/index"),
);
const InquiryNew = lazyWithReload(() => import("./pages/Inquiries/new"));
// PRODUCT_DEV-908: よくある質問（flag inquiryFaq）
const InquiryFaq = lazyWithReload(() => import("./pages/Inquiries/faq"));
const InquiryDetail = lazyWithReload(
  () => import("./pages/Inquiries/[inquiryId]"),
);
const Settings = lazyWithReload(() => import("./pages/Settings/index"));
// PRODUCT_DEV-1225: Google / Microsoft の同意画面から戻る先
const CalendarCallback = lazyWithReload(() => import("./pages/Settings/CalendarCallback"));
const ShortcutInstall = lazyWithReload(
  () => import("./pages/ShortcutInstall/index"),
);
const Chat = lazyWithReload(() => import("./pages/Chat"));
const Memo = lazyWithReload(() => import("./pages/Memo"));
const MemoDetail = lazyWithReload(() => import("./pages/Memo/MemoDetail"));
const MemoTodoDetail = lazyWithReload(() => import("./pages/Memo/MemoTodoDetail"));
const MemoSearch = lazyWithReload(() => import("./pages/Memo/MemoSearch"));
const MemoRecording = lazyWithReload(() => import("./pages/Memo/MemoRecording"));
// PRODUCT_DEV-583: マイ顧客（マイページ）
const MyCustomers = lazyWithReload(() => import("./pages/MyCustomers/index"));
const MyCustomerDetail = lazyWithReload(() => import("./pages/MyCustomers/[companyId]/index"));
const MyCustomerEdit = lazyWithReload(() => import("./pages/MyCustomers/[companyId]/edit"));
const MyCustomersHandover = lazyWithReload(() => import("./pages/MyCustomers/handover"));
// PRODUCT_DEV-586: 議事録
const MinutesRecording = lazyWithReload(
  () => import("./pages/Minutes/MinutesRecording"),
);
const MinutesDetail = lazyWithReload(() => import("./pages/Minutes/MinutesDetail"));
const AdminLayout = lazyWithReload(() => import("./components/shared/AdminLayout"));
const AdminTop = lazyWithReload(() => import("./pages/admin/AdminTop"));
const Users = lazyWithReload(() => import("./pages/admin/Users"));
const UserDetail = lazyWithReload(() => import("./pages/admin/Users/[userId]"));
const TenantSettings = lazyWithReload(() => import("./pages/admin/TenantSettings"));
// PRODUCT_DEV-1093: システム管理者の「テナント設定」（ログイン画面の表示・Webhook 通知）
const SystemTenantSettings = lazyWithReload(() => import("./pages/admin/SystemTenantSettings"));
const AdminPresets = lazyWithReload(() => import("./pages/admin/Presets"));
const AdminPresetDetail = lazyWithReload(() => import("./pages/admin/Presets/[presetId]"));
// PRODUCT_DEV-684: 音声でプリセットを作る（自由記録ベースの往復）
const AdminPresetDraftBuilder = lazyWithReload(() => import("./pages/admin/Presets/DraftBuilder"));
const AdminCompanies = lazyWithReload(() => import("./pages/admin/Companies"));
const AdminCompanyDetail = lazyWithReload(() => import("./pages/admin/Companies/[companyId]"));
const CompanyReport = lazyWithReload(() => import("./pages/admin/Companies/[companyId]/Report"));
const AdminDictionaries = lazyWithReload(() => import("./pages/admin/Dictionaries"));
const AdminDictionaryDetail = lazyWithReload(() => import("./pages/admin/Dictionaries/[dictionaryId]"));
const AdminRealtimeConfigs = lazyWithReload(() => import("./pages/admin/RealtimeConfigs"));
// PRODUCT_DEV-572: 部署管理
const AdminDepartments = lazyWithReload(() => import("./pages/admin/Departments"));
// PRODUCT_DEV-721: 部署の作成・編集（全画面。new と :deptId が同じ画面）
const AdminDepartmentDetail = lazyWithReload(() => import("./pages/admin/Departments/[deptId]"));
// PRODUCT_DEV-640: データ出力（商談結果DL）。sysadmin メニューから遷移。
const AdminDataExport = lazyWithReload(() => import("./pages/admin/DataExport"));
const AdminRealtimeConfigDetail = lazyWithReload(() => import("./pages/admin/RealtimeConfigs/[configId]"));
const Forbidden = lazyWithReload(() => import("./pages/403"));
const NotFound = lazyWithReload(() => import("./pages/404"));

// ─────────────────────────────────────────────────────────────
// Layout Component
// ─────────────────────────────────────────────────────────────
// PRODUCT_DEV-699: 個人設定はサーバが正。MasterData と同じく loader の内側
// （auth 解決済み）に置く。初期値は localStorage の下書きから同期的に決まるので、
// ここでサスペンドはしない。
// PRODUCT_DEV-1070: ロゴの行き先が個人設定（HOME）で決まるので、ChromeProvider より外に置く。
const RootLayout: React.FC = () => (
  <PreferencesProvider>
    <RootChrome />
  </PreferencesProvider>
);

const RootChrome: React.FC = () => {
  /**
   * PRODUCT_DEV-782: 手帳が ON のテナントは**ホームが手帳になる**。
   * PRODUCT_DEV-1070: その中で、個人設定で商談一覧を選んだ人は商談一覧がホーム。
   *
   * ロゴを押したときの行き先と、その読み上げ文言がここで決まる。商談記録の URL（`/`）は
   * 動かさず、「どこをホームと呼ぶか」だけを差し替える。
   */
  const home = useHome();
  const chrome = React.useMemo(
    () => ({ ...unizoChrome, homePath: home.path, homeLabel: home.label }),
    [home.path, home.label],
  );

  return (
    // PRODUCT_DEV-748: 下部ナビ・メニュー・ロゴの実体をここで注入する。
    // Minizo ビルドは別エントリ（router.minizo.tsx）が minizoChrome を注入するため、
    // Unizo 側のナビ・メニューは Minizo の dist に入らない。
    <ChromeProvider value={chrome}>
    <div className="min-h-screen bg-slate-50">
      <EnvironmentBanner />
      <ScrollToTop />
      {/* PRODUCT_DEV-1043: 違う人がログインしたら、来た画面の記録（商談名などが入る）を捨てる */}
      <NavOwnerSync />
      {/* PRODUCT_DEV-612: 遷移がコミットされないまま固まったらフルページロードで脱出する。
          Outlet の外（Suspense の外）に置くこと。中に置くと遷移先と一緒にサスペンドして
          ウォッチドッグ自身が動かなくなる。 */}
      <NavigationWatchdog />
      {/* PRODUCT_DEV-703: 遷移中の唯一のフィードバック（route.lazy は現在の画面を出したまま
          保留するため、これが無いと「押しても何も起きない」に見える）。Suspense の外に置く。 */}
      <NavigationProgress />
      {/* PRODUCT_DEV-783: 商談記録を保存して戻った画面で「本登録しました」を出す（戻り先はどの画面でもよい） */}
      <SavedMeetingToast />
      {/* PRODUCT_DEV-1201: 送った・提出したあとに移った画面で、紙ひこうきのゾウを一度だけ出す */}
      <SentCelebration />
      {/* PRODUCT_DEV-652: 企業/ユーザマスタは loader を待たせず、シェル描画後に取得する。
          Provider をここ（loader の内側 = auth 解決済み）に置くことで tenantId が確実に得られる。 */}
      {/* PRODUCT_DEV-721 U6: まとめメール・通知のリンクの部署で「いまの部署」を切り替える */}
      <ReportDeptLinkSync />
      <MasterDataProvider>
        {/* PRODUCT_DEV-703: ページは route.lazy へ移したので通常はここでサスペンドしない。
            ページ内部の React.lazy / use() に対する安全網として残す。 */}
        <Suspense fallback={<Splash />}>
          <Outlet />
        </Suspense>
      </MasterDataProvider>
    </div>
    </ChromeProvider>
  );
};

// PRODUCT_DEV-583 R5/614: メンバー向け顧客マスタは「顧客一覧」(/my-customers) に
// 統合済み（100%反映）のため、/companies 系は常に顧客一覧へリダイレクトする
// （:companyId 付きは対応する顧客詳細へ）。/admin/companies は対象外。
const LegacyCompaniesRoute: React.FC = () => {
  const { companyId } = useParams();
  const to = companyId
    ? ROUTE_MY_CUSTOMERS_DETAIL.replace(":companyId", encodeURIComponent(companyId))
    : ROUTE_MY_CUSTOMERS;
  return <Navigate to={to} replace />;
};

// PRODUCT_DEV-622: 顧客レポートを管理画面レイアウトの外（単体ページ）へ移した。
// 既存のブックマーク・過去の商談詳細からの戻り先が死なないようリダイレクトを残す。
const LegacyReportRoute: React.FC = () => {
  const { companyId } = useParams();
  if (!companyId) return <Navigate to={ROUTE_MY_CUSTOMERS} replace />;
  return (
    <Navigate
      to={ROUTE_COMPANY_REPORT.replace(":companyId", encodeURIComponent(companyId))}
      replace
    />
  );
};

// ─────────────────────────────────────────────────────────────
// Router Configuration
// ─────────────────────────────────────────────────────────────

// PRODUCT_DEV-1043: 履歴の番号が振り直された（新しい文書）かは router を作る前にしか分からない
prepareHistory();

export const router = createBrowserRouter([
  {
    path: import.meta.env.BASE_URL,
    id: "root",
    loader: rootLoader,
    // PRODUCT_DEV-652 / 702 / 703: react-router の既定ではナビゲーションのたびに loader が
    // 再実行される。root-loader は whoami + 設定系 6 本（presets 261kB 等）を取り、
    // loader は遷移をブロックするため、そのまま体感の待ち時間になっていた。
    // 現在は「遷移では取り直さず、マスタを書き換えたときに取り直す」に統一している
    // （判定とテストは shouldRevalidateRoot.ts / loaders/rootMasters.ts を参照）。
    shouldRevalidate: (args) => shouldRevalidateRoot(args),
    element: <RootLayout />,
    children: [
      // PRODUCT_DEV-782: 手帳が ON のテナントでは、素の `/` を root-loader が手帳へ送る。
      // ここは商談記録一覧のまま（絞り込み付きの `/?...` はこちらに来る）。
      // 一覧に必ず当たる URL として `/meetings-list` も置く（下タブの「商談記録」が指す先）。
      { index: true, lazy: Dashboard, handle: screen("meetingsList") },
      { path: "meetings-list", lazy: Dashboard, handle: screen("meetingsList") },
      { path: "recording", lazy: Recording, handle: screen("recording", { transient: true }) },
      { path: "speed-recording", lazy: SpeedRecording, handle: screen("speedRecording", { transient: true }) },
      { path: "copy-record-setup", lazy: CopyRecordSetup, handle: screen("copyRecordSetup", { transient: true }) },
      { path: "meetings/:id", lazy: MeetingDetail, handle: screen("meetingDetail") },
      { path: "meeting-groups/create", lazy: MeetingGroupCreate, handle: screen("groupCreate", { transient: true }) },
      { path: "meeting-groups/:groupId", lazy: MeetingGroupDetail, handle: screen("groupDetail") },
      { path: "guide", lazy: Guide, handle: screen("guide") },
      // 戻り先にはしない（練習の途中の画面へ戻らせない）
      { path: "onboarding", lazy: Onboarding, handle: screen("onboarding", { transient: true }) },
      {
        path: "guide/article/:categoryId/:articleId",
        lazy: GuideArticleDetail,
        handle: screen("guideArticle"),
      },
      { path: "guide/scene/:sceneId", lazy: GuideSceneDetail, handle: screen("guideScene") },
      { path: "announcements", lazy: Announcements, handle: screen("announcements") },
      { path: "trash", lazy: Trash, handle: screen("trash") },
      { path: "notifications", lazy: Notifications, handle: screen("notifications") },
      // PRODUCT_DEV-635: 問い合わせ（静的パス new を動的パスより先に定義）
      { path: "mypage", lazy: MyPage, handle: screen("mypage") },
      { path: "mypage/summary", lazy: ActivitySummaryScreen, handle: screen("summary") },
      { path: "mypage/archive", lazy: ArchiveScreen, handle: screen("archive") },
      // PRODUCT_DEV-675: 静的セグメントを :scheduleId より先に置く（既存の慣習どおり）
      { path: "mypage/scheduling", lazy: SchedulingListScreen, handle: screen("schedulingList") },
      // 静的セグメントを :scheduleId より先に置く
      { path: "mypage/scheduling/new", lazy: SchedulingNewScreen, handle: screen("schedulingNew", { transient: true }) },
      { path: "mypage/scheduling/:scheduleId", lazy: SchedulingDetailScreen, handle: screen("schedulingDetail") },
      {
        // 確定後の日時変更（PRODUCT_DEV-675 PR9）。詳細から遷移する
        path: "mypage/scheduling/:scheduleId/reschedule",
        lazy: SchedulingRescheduleScreen,
        handle: screen("schedulingReschedule"),
      },
      {
        // 確定前の候補日時の編集（PRODUCT_DEV-675）。詳細から遷移する
        path: "mypage/scheduling/:scheduleId/edit",
        lazy: SchedulingEditScreen,
        handle: screen("schedulingEdit"),
      },
      // PRODUCT_DEV-1043: 管理者ビューもマイページ（手帳）と同じ画面として扱う（表示の切り替えで行き来し、
      // ← の名前もマイページ／マイ手帳で通っている）
      { path: "mypage/admin", lazy: AdminSummaryScreen, handle: screen("manager") },
      // 静的セグメントより後ろに置く（router.tsx の慣習）
      {
        path: "mypage/reports/:period/:periodKey",
        lazy: ReportScreen,
        handle: screen("report"),
      },
      { path: "inquiries", lazy: Inquiries, handle: screen("inquiries") },
      { path: "inquiries/new", lazy: InquiryNew, handle: screen("inquiryNew") },
      // 静的パスは動的パス（:inquiryId）より先に置く
      { path: "inquiries/faq", lazy: InquiryFaq, handle: screen("inquiryFaq") },
      // PRODUCT_DEV-908: AI に質問する画面は問い合わせフォームに1本化した（ブックマーク対策の redirect）
      { path: "inquiries/qa", element: <Navigate to={ROUTE_INQUIRIES_NEW} replace /> },
      { path: "inquiries/:inquiryId", lazy: InquiryDetail, handle: screen("inquiryDetail") },
      { path: "settings", lazy: Settings, handle: screen("settings") },
      // PRODUCT_DEV-1225: 同意画面からの戻り先。戻り先にしない画面（← で戻ってこない）
      { path: "settings/calendar/callback", lazy: CalendarCallback, handle: screen("settings", { transient: true }) },
      { path: "shortcut-install", lazy: ShortcutInstall, handle: screen("shortcutInstall") },
      { path: "chat", lazy: Chat, handle: screen("chat") },
      { path: "memo", lazy: Memo, handle: screen("memo") },
      { path: "memo/search", lazy: MemoSearch, handle: screen("memoSearch") },
      { path: "memo/recording", lazy: MemoRecording, handle: screen("memoRecording", { transient: true }) },
      { path: "memo/todo/:todoId", lazy: MemoTodoDetail, handle: screen("todoDetail") },
      { path: "memo/:memoId", lazy: MemoDetail, handle: screen("memoDetail") },
      // PRODUCT_DEV-583: マイ顧客（マイページ）。handover を :companyId より前に登録。
      { path: "my-customers", lazy: MyCustomers, handle: screen("myCustomers") },
      { path: "my-customers/handover", lazy: MyCustomersHandover, handle: screen("handover") },
      { path: "my-customers/:companyId", lazy: MyCustomerDetail, handle: screen("customerDetail") },
      { path: "my-customers/:companyId/edit", lazy: MyCustomerEdit, handle: screen("customerEdit") },
      // PRODUCT_DEV-586: 議事録（録音は静的パス→動的パスより先に定義）
      { path: "minutes/recording", lazy: MinutesRecording, handle: screen("minutesRecording", { transient: true }) },
      { path: "minutes/:minutesId", lazy: MinutesDetail, handle: screen("minutesDetail") },
      // PRODUCT_DEV-583 R5/614: メンバー向け /companies は顧客一覧へ常時リダイレクト
      // （admin/companies は下の admin ルートで従来どおり維持）。
      // PRODUCT_DEV-622: 顧客レポート（管理メニューを持たない単体ページ）
      { path: "customer-report/:companyId", lazy: CompanyReport, handle: screen("customerReport") },
      { path: "companies", element: <LegacyCompaniesRoute /> },
      { path: "companies/:companyId", element: <LegacyCompaniesRoute /> },
      // PRODUCT_DEV-419 follow-up: メンバー向け辞書管理（admin/dictionariesと同じコンポーネントを共有）
      { path: "dictionaries", lazy: AdminDictionaries, handle: screen("dictionaries") },
      { path: "dictionaries/:dictionaryId", lazy: AdminDictionaryDetail, handle: screen("dictionaryDetail") },
      {
        path: "admin",
        lazy: AdminLayout,
        // PRODUCT_DEV-1043: 管理画面は「来た画面へ戻る」の対象外（記録だけする）
        handle: screen("admin"),
        children: [
          { index: true, lazy: AdminTop },
          // admin向け（一般管理者でもアクセス可能）
          { path: "companies", lazy: AdminCompanies },
          { path: "companies/:companyId", lazy: AdminCompanyDetail },
          { path: "companies/:companyId/report", element: <LegacyReportRoute /> },
          { path: "dictionaries", lazy: AdminDictionaries },
          { path: "dictionaries/:dictionaryId", lazy: AdminDictionaryDetail },
          // PRODUCT_DEV-572: 部署管理はテナント管理者(admin)に解放
          { path: "departments", lazy: AdminDepartments },
          // 静的セグメントは :deptId より優先されるため順序に依存しない。
          // ⚠ **`departments` と同じ親（この `/admin` 配下）から出さないこと。** 作成・編集ページは
          // 自前のガードを持たず、ここに置かれていることだけが admin 限定の根拠になっている
          { path: "departments/new", lazy: AdminDepartmentDetail },
          { path: "departments/:deptId", lazy: AdminDepartmentDetail },
          // PRODUCT_DEV-1093: テナント設定はログイン画面の表示・Webhook 以外を admin へ降ろした
          // （`tenantSettings.general`。ここに置かれていることが admin 以上の根拠。サーバも同じ表で判定）
          { path: "tenant-settings", lazy: TenantSettings },
          // PRODUCT_DEV-721: ユーザー管理・パスワード再設定・データ出力をテナント管理者(admin)へ
          // 降ろした（9/8 プロダクト要件会議）。**機能フラグ `managerRole` が ON のテナントだけ**で、
          // OFF のあいだは従来どおり sysadmin 限定（ガードが切り替える）。
          // ⚠ specialFlags の付与はフラグに関わらず sysadmin 限定（画面・API の両方で弾く）。
          {
            element: <RoleSplitDescendedGuard capability="users.manage" />,
            children: [
              { path: "users", lazy: Users },
              { path: "users/:userId", lazy: UserDetail },
            ],
          },
          {
            element: <RoleSplitDescendedGuard capability="data.export" />,
            children: [{ path: "data-export", lazy: AdminDataExport }],
          },
          // sysadmin限定
          {
            element: <SysadminGuard />,
            children: [
              // PRODUCT_DEV-1093: テナント設定のうちログイン画面の表示・Webhook 通知
              { path: "system-tenant-settings", lazy: SystemTenantSettings },
              { path: "presets", lazy: AdminPresets },
              // 静的セグメントは :presetId より優先されるため順序に依存しない
              { path: "presets/voice", lazy: AdminPresetDraftBuilder },
              { path: "presets/new", lazy: AdminPresetDetail },
              { path: "presets/:presetId", lazy: AdminPresetDetail },
              { path: "realtime-configs", lazy: AdminRealtimeConfigs },
              { path: "realtime-configs/new", lazy: AdminRealtimeConfigDetail },
              { path: "realtime-configs/:configId", lazy: AdminRealtimeConfigDetail },
            ],
          },
        ],
      },
      {
        path: "403",
        lazy: Forbidden,
      },
      {
        path: "*",
        lazy: NotFound,
      },
    ],
  },
  // PRODUCT_DEV-675: 相手（未ログイン）の回答画面 — **rootLoader の外に置く唯一のルート**。
  //
  // root の children に入れると rootLoader が走り、未ログインの相手が Portal SSO へ
  // 飛ばされてしまう。「相手がログインせずに応答できる」ことがこの機能の価値そのもの
  // なので、loader を持たない兄弟ルートとして並べる。
  //
  // ⚠ この配下のコンポーネントは `components/shared/` を一切使えない
  //   （`MobileLayout` 等が `useRootLoader()` を呼ぶため、root context が無いと落ちる）。
  //
  // CloudFront は /assets 等以外を /index.html に書き換えるので CDK 側の追加設定は不要。
  // 認可はワンタイムトークンが担う（API も /public/scheduling/* だけが Authorizer 無し）。
  {
    path: ROUTE_GUEST_SCHEDULING,
    lazy: GuestSchedulingScreen,
  },
]);

// PRODUCT_DEV-1043: 来た画面の記録（左上の「← ○○」を来た画面へ戻すため）
installBackTracker(router, readScreenHandle, "/");

/**
 * PRODUCT_DEV-612: ウォッチドッグ発火時に react-router 自身の状態を読めるようにする。
 *
 * フック（`useLocation` / `useNavigation`）では取れない。遷移がコミットされないときは
 * フックの値も同じく更新されないため、「react-router がまだ解決していない」のか
 * 「react-router は完了していて React がコミットしていない」のかを区別できない。
 * `router.state` は React のコミットとは無関係に同期更新されるので、ここを直接読む。
 *
 * 注入式にしているのは、`navigationWatchdog` が `router.tsx` を import すると
 * 循環参照になるため（`router.tsx` → `NavigationWatchdog` → `navigationWatchdog`）。
 */
setRouterStateReader(() => {
  const { navigation, location, historyAction } = router.state;
  return {
    navigationState: navigation.state,
    navigatingTo: navigation.location
      ? navigation.location.pathname + navigation.location.search
      : null,
    routerLocation: location.pathname + location.search,
    historyAction: historyAction ?? null,
  };
});
